import { memo } from 'react'
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  ChartData,
  ChartOptions,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import { IChatAnalyticsSummary } from '../models'
import {
  buildDailyChart,
  buildDailyQuestionChart,
  buildFeedbackReasonChart,
  buildFeedbackTrendChart,
  buildInputModeChart,
  buildOutcomeChart,
  hasDailyQuestionMetrics,
} from '../viewModel'
import styles from './ChatbotAnalytics.module.scss'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend)

const lineOptions: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: { legend: { position: 'bottom' } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
}

const doughnutOptions: ChartOptions<'doughnut'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
}

const barOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
}

/** Sütunlar yığılır (yazılı + buton = günlük soru); aktif bayi sağ eksende ayrı ölçekte. */
const dailyQuestionOptions: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: { legend: { position: 'bottom' } },
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      beginAtZero: true,
      ticks: { precision: 0 },
      title: { display: true, text: 'Soru' },
    },
    y1: {
      position: 'right',
      beginAtZero: true,
      ticks: { precision: 0 },
      grid: { drawOnChartArea: false },
      title: { display: true, text: 'Aktif bayi' },
    },
  },
}

interface ChatbotAnalyticsChartsProps {
  data: IChatAnalyticsSummary
}

function ChatbotAnalyticsCharts({ data }: ChatbotAnalyticsChartsProps) {
  return (
    <div className={styles.chartGrid}>
      {/* Eski backend günlük soru alanlarını göndermez; o durumda sıfırlarla yanıltıcı grafik çizilmez. */}
      {hasDailyQuestionMetrics(data) ? (
        <section
          className={`${styles.panel} ${styles.trendPanel}`}
          aria-labelledby='chatbot-daily-questions-title'
          data-testid='daily-question-chart'
        >
          <div className={styles.panelHeader}>
            <div>
              <span className={styles.eyebrow}>Günlük görünüm · Türkiye saati</span>
              <h2 id='chatbot-daily-questions-title'>Günlük Soru ve Bayi Sayısı</h2>
            </div>
          </div>
          <div
            className={styles.chartCanvas}
            role='img'
            aria-label='Günlük yazılı soru, buton tıklaması ve aktif bayi grafiği'
          >
            {/* Karma grafik: Bar bileşeni, çizgi serisini dataset `type: 'line'` ile çizer. */}
            <Bar
              data={buildDailyQuestionChart(data) as unknown as ChartData<'bar', number[], string>}
              options={dailyQuestionOptions}
            />
          </div>
        </section>
      ) : null}

      <section className={`${styles.panel} ${styles.trendPanel}`} aria-labelledby='chatbot-daily-trend-title'>
        <div className={styles.panelHeader}>
          <div>
            <span className={styles.eyebrow}>Günlük görünüm</span>
            <h2 id='chatbot-daily-trend-title'>Tur Eğilimi</h2>
          </div>
        </div>
        <div
          className={styles.chartCanvas}
          role='img'
          aria-label='Günlük toplam, teknik başarılı ve hatalı tur grafiği'
        >
          <Line data={buildDailyChart(data)} options={lineOptions} />
        </div>
      </section>

      <section className={styles.panel} aria-labelledby='chatbot-outcome-title'>
        <div className={styles.panelHeader}>
          <div>
            <span className={styles.eyebrow}>Sonuç kırılımı</span>
            <h2 id='chatbot-outcome-title'>Outcome Dağılımı</h2>
          </div>
        </div>
        <div className={styles.chartCanvas} role='img' aria-label='Chatbot sonuç dağılımı grafiği'>
          <Doughnut data={buildOutcomeChart(data)} options={doughnutOptions} />
        </div>
      </section>

      <section className={styles.panel} aria-labelledby='chatbot-input-mode-title'>
        <div className={styles.panelHeader}>
          <div>
            <span className={styles.eyebrow}>Kullanım biçimi</span>
            <h2 id='chatbot-input-mode-title'>Giriş Modu</h2>
          </div>
        </div>
        <div className={styles.chartCanvas} role='img' aria-label='Buton ve serbest metin giriş modu grafiği'>
          <Bar data={buildInputModeChart(data)} options={barOptions} />
        </div>
      </section>

      {/* Eski backend `Feedback` göndermeyebilir; grafikler o durumda hiç çizilmez. */}
      {(data.Feedback?.TotalVotes ?? 0) > 0 && (
        <>
          <section className={`${styles.panel} ${styles.trendPanel}`} aria-labelledby='chatbot-feedback-trend-title'>
            <div className={styles.panelHeader}>
              <div>
                <span className={styles.eyebrow}>Günlük görünüm</span>
                <h2 id='chatbot-feedback-trend-title'>Geri Bildirim Eğilimi</h2>
              </div>
            </div>
            <div className={styles.chartCanvas} role='img' aria-label='Günlük olumlu ve olumsuz oy grafiği'>
              <Line data={buildFeedbackTrendChart(data)} options={lineOptions} />
            </div>
          </section>

          <section className={styles.panel} aria-labelledby='chatbot-feedback-reason-title'>
            <div className={styles.panelHeader}>
              <div>
                <span className={styles.eyebrow}>Olumsuz oy nedeni</span>
                <h2 id='chatbot-feedback-reason-title'>Neden Dağılımı</h2>
              </div>
            </div>
            <div className={styles.chartCanvas} role='img' aria-label='Olumsuz oy neden dağılımı grafiği'>
              <Bar data={buildFeedbackReasonChart(data)} options={barOptions} />
            </div>
          </section>
        </>
      )}
    </div>
  )
}

export default memo(ChatbotAnalyticsCharts)
